import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { Alert, Button, Field, TextInput } from '../components/ui.js';
import { MIN_PASSWORD_LENGTH_HINT } from '../lib/passwordHint.js';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest('/api/auth/password-reset/complete', {
        method: 'POST',
        body: { token, password },
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Your password could not be reset. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-slate-900">Choose a new password</h1>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
          {token === '' ? (
            <div className="flex flex-col gap-4">
              <Alert variant="error" title="Link not valid">
                This reset link is missing its token.
              </Alert>
              <Link className="text-sm text-brand-700 hover:underline" to="/forgot-password">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
              {error && <Alert variant="error">{error}</Alert>}

              <p className="text-sm text-slate-600">
                Setting a new password signs you out everywhere else.
              </p>

              <Field
                label="New password"
                htmlFor="password"
                required
                hint={MIN_PASSWORD_LENGTH_HINT}
              >
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              <Field label="Confirm password" htmlFor="confirm" required>
                <TextInput
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </Field>

              <Button type="submit" loading={submitting} className="w-full">
                Set new password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
